import React, { useEffect, useState } from 'react';
import { isEmpty, map } from 'lodash';
import { motion } from 'framer-motion';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import BoxedContainer from './layouts/BoxedContainer';
import Sprites from '../components/Sprites';

async function fetchFileSize(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const sizeInMB = (blob.size / (1024 * 1024)).toFixed(2);
    return sizeInMB;
  } catch (error) {
    console.error(`Error fetching file size for ${url}:`, error);
    return 'Unknown';
  }
}

export default function FreeZoneTwoColTextContentWithTwoCards({ id, data }) {
  const { title, text, downloadFiles, items, backgroundImage } = data;
  const [fileSizes, setFileSizes] = useState([]);
  const [itemFileSizes, setItemFileSizes] = useState([]);

  useEffect(() => {
    if (downloadFiles && downloadFiles.length) {
      const fetchSizes = async () => {
        const sizes = await Promise.all(downloadFiles.map((file) => fetchFileSize(file.url)));
        setFileSizes(sizes);
      };
      fetchSizes();
    }
  }, [downloadFiles]);

  useEffect(() => {
    if (items && items.length) {
      const fetchItemSizes = async () => {
        const sizes = await Promise.all(items.map((item) => fetchFileSize(item.content.link.url)));
        setItemFileSizes(sizes);
      };
      fetchItemSizes();
    }
  }, [items]);
  return (
    <section id={id} className="relative overflow-hidden">
      <div
        className="absolute right-0 lg:ltr:right-auto lg:ltr:left-0 top-[85px] 2xl:top-[-40px]
        hidden xl:flex rtl:justify-start rtl:lg:justify-end ltr:justify-end ltr:lg:justify-start
        rtl:scale-[-1]">
        <Sprites
          name="freezone-corner-arrow"
          className="w-[20%] 2xl:w-[63%] scale-[-1] lg:scale-100"
        />
      </div>
      <BoxedContainer
        className="pt-[50px] md:py-[100px] flex flex-col gap-y-[70px]
      md:gap-y-[137px] z-10 relative">
        <div className="flex flex-col gap-7 md:flex-row md:gap-[60px]">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            viewport={{ once: true }}
            dangerouslySetInnerHTML={{ __html: title }}
            className="text-clamp20to40  h2-child:text-clamp20to40 text-[#231F20]
            h2-child:leading-1.2 md:max-w-[540px]
             h2-child:m-0"
          />
          <div className="w-[50px] h-[2px] bg-[#CE445B] md:hidden mt-[-8px]" />
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            viewport={{ once: true }}
            className="md:max-w-[400px] lg:max-w-[570px] flex flex-col gap-y-7 md:gap-y-[45px]">
            <div
              dangerouslySetInnerHTML={{ __html: text }}
              className="p-child:text-[#4D4D4D] p-child:text-clamp12to18 p-child:font-normal
            p-child:leading-[1.6] p-child:mb-0 p-child:ltr:font-URWDINRegular p-child:rtl:font-GESSTwoDWTCLight flex flex-col gap-y-5
            md:gap-y-8"
            />
            {isEmpty(downloadFiles) || (
              <div className="grid lg:grid-cols-2 gap-[25px] md:gap-[30px]">
                {map(
                  downloadFiles,
                  (downloadFile, index) =>
                    isEmpty(downloadFile) || (
                      <a
                        key={index}
                        href={downloadFile.content.file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="pdf-download-card-cta flex flex-row gap-x-3 md:gap-x-[15px] 
                        items-start">
                        <Sprites
                          name="freezone-download-button"
                          className="min-w-[25px] md:min-w-[32px] w-[25px] md:w-[32px] relative ltr:top-[-7px] md:ltr:top-[-4px]"
                        />
                        <div className="flex flex-col gap-[10px]">
                          <span className="text-clamp14to18 text-black ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight ltr:leading-1 rtl:leading-[1.3]">
                            {downloadFile.content.file.name}
                          </span>
                          <span className="text-[13px] md:text-[15px] text-[#4A4A4A] leading-1 font-DINRegular">
                            PDF • {downloadFile.content.fileSize}
                          </span>
                        </div>
                      </a>
                    ),
                )}
              </div>
            )}
          </motion.div>
        </div>
        {isEmpty(items) || (
          <div className="flex flex-col md:flex-row gap-[54px] md:gap-8">
            {map(items, (item, index1) => (
              <motion.div
                key={index1}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3 * (index1 + 1) }}
                className="bg-white px-5 md:px-[30px] pb-7 md:pb-10 pt-12 md:pt-[60px]
                border-t-[2px] border-t-[#CE445B] flex flex-col gap-y-5 md:gap-y-6 relative">
                <div
                  className="w-[60px] md:w-20 h-[60px] md:h-20 rounded-full border-[2px]
                    border-[#CE445B] flex justify-center items-center absolute top-[-35px]
                    -translate-x-1/2 left-1/2 bg-white">
                  <Sprites name={item.content.cardIconName} className="w-[34px] md:w-[50px]" />
                </div>
                <div className="flex flex-col gap-y-5 md:gap-y-7">
                  <h4
                    className=" ltr:font-LibreBaskervilleRegular rtl:font-GESSTwoDWTCMedium  text-clamp14to20 text-[#58595B]
                  leading-1.5">
                    {item.content.cardTitle}
                  </h4>
                  {isEmpty(item.content.link) || (
                    <a
                      href={item.content.link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="pdf-download-card-cta 
                      flex flex-row gap-x-3 md:gap-x-[15px] items-start">
                      <Sprites
                        name="freezone-download-button"
                        className="w-[25px] md:w-[32px] relative ltr:top-[-7px] md:ltr:top-[-4px]"
                      />
                      <div className="flex flex-col gap-[10px]">
                        <span className="text-clamp14to18 text-black ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight ltr:leading-1 rtl:leading-[1.3]">
                          {item.content.link.name}
                        </span>
                        <span className="text-[13px] md:text-[15px] text-[#4A4A4A] leading-1 font-DINRegular">
                          PDF • {item.content.fileSize}
                        </span>
                      </div>
                    </a>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: item.content.cardText }}
                  className="p-child:text-[#4A4A4A] p-child:text-clamp12to16 p-child:font-normal
                  p-child:leading-[1.6] p-child:mb-0 p-child:ltr:font-URWDINRegular p-child:rtl:font-GESSTwoDWTCLight flex flex-col gap-2
                  md:gap-5"
                />
              </motion.div>
            ))}
          </div>
        )}
      </BoxedContainer>
      {isEmpty(backgroundImage) || (
        <div className="w-full h-full absolute top-0 lazy-load-image:flex lazy-load-image:h-full">
          <LazyLoadImage
            src={backgroundImage.url}
            width="100%"
            effect="blur"
            placeholderSrc={`${backgroundImage}`}
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </section>
  );
}
